import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { productService } from '../../services/productService';
import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import Checkbox from '../../components/common/Checkbox';
import Card from '../../components/common/Card';
import Skeleton from '../../components/common/Skeleton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '../../components/common/Tabs';
import VariationManager from '../../components/supplier/VariationManager';
import ProductImagesUploader from '../../components/supplier/ProductImagesUploader';
import useKeyboard from '../../hooks/useKeyboard';

async function loadCategoriesForCurrentStore() {
  return api.get('/api/categories');
}

function mapVariationToApi(v) {
  // size/color vivem em attributes (JSON livre), mas name é coluna dedicada.
  // Mescla com attributes existente pra preservar chaves customizadas (futuro).
  const attributes = {};
  if (v.size) attributes.size = v.size;
  if (v.color) attributes.color = v.color;
  if (v.attributes && typeof v.attributes === 'object') {
    for (const [k, val] of Object.entries(v.attributes)) {
      if (k !== 'size' && k !== 'color') attributes[k] = val;
    }
  }
  return {
    sku: v.sku || null,
    name: v.name ? String(v.name).trim() : null,
    attributes: Object.keys(attributes).length ? attributes : null,
    stock: Number(v.stock) || 0,
    cost: Number(v.cost) || 0,
    price: Number(v.price) || 0,
    commissionPercent: v.commissionPercent != null ? Number(v.commissionPercent) : 0,
    resellerPrice: v.resellerPrice != null ? Number(v.resellerPrice) : undefined,
    active: v.active !== false,
  };
}

// Backend devolve size/color dentro de attributes. Pra simplificar o estado
// local do form, achata pra forma flat — VariationManager opera só em campos
// top-level. Parse defensivo porque MariaDB JSON volta string em alguns paths
// (mesmo problema do theme da Store / images do Product).
function normalizeVariationFromApi(v) {
  let attrs = v.attributes;
  if (typeof attrs === 'string') {
    try { attrs = JSON.parse(attrs); } catch { attrs = {}; }
  }
  if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs)) attrs = {};
  return {
    ...v,
    attributes: attrs,
    name: v.name || '',
    size: attrs.size || '',
    color: attrs.color || '',
  };
}

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { currentStore } = useAuth();
  const isEditing = !!id;
  // Loja reseller pode definir comissão padrão; o form pré-preenche o campo da
  // variação. Outros tipos: ignoramos (default = null).
  const defaultCommissionPercent = currentStore?.businessType === 'reseller'
    && currentStore?.defaultCommissionPercent != null
    ? Number(currentStore.defaultCommissionPercent)
    : null;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState('basics');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    brand: '',
    images: [],
    variations: [],
    active: true,
    featured: false,
  });
  // Snapshot do form depois que carrega (ou inicial pra novos). Comparação
  // contra o estado atual define `isDirty` — usado pra perguntar antes de
  // sair sem salvar.
  const [pristineSnapshot, setPristineSnapshot] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const skipDirtyGuardRef = useRef(false);

  const loadCategories = useCallback(async () => {
    try {
      const cats = await loadCategoriesForCurrentStore();
      setCategories(Array.isArray(cats) ? cats : cats?.rows || []);
    } catch {
      /* silently ignore; user pode salvar sem categoria */
    }
  }, []);

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      const product = await productService.getById(id);
      const next = {
        name: product.name || '',
        description: product.description || '',
        categoryId: product.categoryId || '',
        brand: product.brand || '',
        images: Array.isArray(product.images) ? product.images.filter(Boolean) : [],
        variations: Array.isArray(product.variations)
          ? product.variations.map(normalizeVariationFromApi)
          : [],
        active: product.active !== false,
        featured: !!product.featured,
      };
      setFormData(next);
      setPristineSnapshot(next);
    } catch (err) {
      error(err.message || 'Erro ao carregar produto');
    } finally {
      setLoading(false);
    }
  }, [id, error]);

  useEffect(() => {
    loadCategories();
    if (isEditing) {
      loadProduct();
    } else {
      // Pra novos produtos, snapshot inicial é o form vazio.
      setPristineSnapshot((prev) => prev || {
        name: '', description: '', categoryId: '', brand: '',
        images: [], variations: [], active: true, featured: false,
      });
    }
  }, [loadCategories, loadProduct, isEditing]);

  const isDirty = useMemo(() => {
    if (!pristineSnapshot) return false;
    return JSON.stringify(formData) !== JSON.stringify(pristineSnapshot);
  }, [formData, pristineSnapshot]);

  // Avisa em fechamento da aba quando há alterações não salvas.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty || skipDirtyGuardRef.current) return undefined;
      e.preventDefault();
      // Browsers ignoram a string e mostram diálogo padrão, mas precisa setar.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // Navegação interna guardada: se o form está sujo, abre confirm; se não,
  // navega direto. Após confirmar, libera o guard pra próxima rodada.
  const guardedNavigate = useCallback((to) => {
    if (isDirty && !skipDirtyGuardRef.current) {
      setPendingNavigation(to);
      return;
    }
    navigate(to);
  }, [isDirty, navigate]);

  const confirmDiscard = () => {
    skipDirtyGuardRef.current = true;
    const target = pendingNavigation;
    setPendingNavigation(null);
    if (target) navigate(target);
  };
  const cancelDiscard = () => setPendingNavigation(null);

  const handleField = (field) => (e) => {
    const value = typeof e === 'string' || typeof e === 'boolean' ? e : e.target?.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImagesChange = (images) => setFormData((prev) => ({ ...prev, images }));

  // ⌘S salva o form. useKeyboard já ignora inputs, mas aqui queremos
  // interceptar mesmo dentro do form — mod+s é combo raro em digitação.
  useKeyboard({ 'mod+s': () => handleSubmit() });

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!formData.name.trim()) {
      error('Nome do produto é obrigatório');
      setTab('basics');
      return;
    }
    if (!formData.variations || formData.variations.length === 0) {
      error('Adicione pelo menos uma variação');
      setTab('variations');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        categoryId: formData.categoryId ? Number(formData.categoryId) : null,
        brand: formData.brand || null,
        images: formData.images,
        active: formData.active,
        featured: formData.featured,
      };

      let productId;
      if (isEditing) {
        const updated = await productService.update(id, payload);
        productId = updated.id;
        for (const v of formData.variations) {
          const apiShape = mapVariationToApi(v);
          if (v.id && typeof v.id === 'number') {
            await productService.updateVariation(v.id, apiShape);
          } else {
            await productService.createVariation(productId, apiShape);
          }
        }
      } else {
        const created = await productService.create(payload);
        productId = created.id;
        for (const v of formData.variations) {
          await productService.createVariation(productId, mapVariationToApi(v));
        }
      }

      success(isEditing ? 'Produto atualizado!' : 'Produto criado!');
      // Salvou — limpa o guard pra navegação não disparar o confirm.
      skipDirtyGuardRef.current = true;
      navigate('/fornecedor/painel/produtos');
    } catch (err) {
      error(err.message || 'Erro ao salvar produto.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <Skeleton variant="text" width="33%" />
        <Skeleton variant="block" height={120} />
        <Skeleton variant="block" height={240} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-5xl mx-auto pb-24">
      <button
        type="button"
        onClick={() => guardedNavigate('/fornecedor/painel/produtos')}
        className="inline-flex items-center gap-2 text-sm text-app-text-secondary hover:text-app-text mb-4 transition-colors"
      >
        <ArrowLeft size={16} /> Voltar para produtos
      </button>
      <h1 className="text-3xl font-extrabold text-app-text mb-6">
        {isEditing ? 'Editar produto' : 'Novo produto'}
      </h1>

      <Tabs value={tab} onChange={setTab}>
        <TabList>
          <Tab value="basics">Dados</Tab>
          <Tab value="images">Imagens {formData.images.length > 0 && `(${formData.images.length})`}</Tab>
          <Tab value="variations">
            Variações {formData.variations.length > 0 && `(${formData.variations.length})`}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="basics">
            <Card className="p-6 space-y-4">
              <Input
                label="Nome do produto"
                name="name"
                value={formData.name}
                onChange={handleField('name')}
                placeholder="Ex: Camiseta Premium"
                required
              />
              <Textarea
                label="Descrição"
                value={formData.description}
                onChange={handleField('description')}
                rows={5}
                placeholder="Descreva o produto detalhadamente…"
                autoResize
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Categoria"
                  value={formData.categoryId}
                  onChange={handleField('categoryId')}
                  options={[
                    { value: '', label: 'Sem categoria' },
                    ...categories.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                />
                <Input
                  label="Marca (opcional)"
                  name="brand"
                  value={formData.brand}
                  onChange={handleField('brand')}
                  placeholder="Ex: Nike"
                />
              </div>
              <div className="flex gap-6 pt-2">
                <Checkbox
                  label="Ativo (visível no catálogo)"
                  checked={formData.active}
                  onChange={(e) => setFormData((p) => ({ ...p, active: e.target.checked }))}
                />
                <Checkbox
                  label="Em destaque"
                  checked={formData.featured}
                  onChange={(e) => setFormData((p) => ({ ...p, featured: e.target.checked }))}
                />
              </div>
            </Card>
          </TabPanel>

          <TabPanel value="images">
            <Card className="p-6">
              <h2 className="text-lg font-bold text-app-text mb-1">Fotos do produto</h2>
              <p className="text-sm text-app-text-secondary mb-4">
                Adicione JPG/PNG/WebP (até 5MB cada). Arraste pra reordenar — a primeira foto vira a principal.
              </p>
              <ProductImagesUploader images={formData.images} onChange={handleImagesChange} />
            </Card>
          </TabPanel>

          <TabPanel value="variations">
            <Card className="p-6">
              <h2 className="text-lg font-bold text-app-text mb-1">Variações</h2>
              <p className="text-sm text-app-text-secondary mb-4">
                Configure as variações (tamanhos, cores, etc.) com preços e estoque.
              </p>
              <VariationManager
                variations={formData.variations}
                onChange={(variations) => setFormData((p) => ({ ...p, variations }))}
                defaultCommissionPercent={defaultCommissionPercent}
              />
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Ações sticky no bottom */}
      <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white border-t border-app-border p-4 z-20">
        <div className="max-w-5xl mx-auto flex justify-end gap-2">
          <Button
            type="button"
            variant="subtle"
            onClick={() => guardedNavigate('/fornecedor/painel/produtos')}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={saving} leftIcon={<Save size={16} />}>
            {isEditing ? 'Salvar alterações' : 'Criar produto'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!pendingNavigation}
        onClose={cancelDiscard}
        onConfirm={confirmDiscard}
        title="Sair sem salvar?"
        message="Você tem alterações não salvas. Se sair agora, elas serão perdidas."
        confirmLabel="Sair sem salvar"
        cancelLabel="Continuar editando"
        variant="danger"
      />
    </form>
  );
};

export default ProductForm;
